import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { DateSheet } from '@/components/calendar/DateSheet'
import { ComingUp, DayCards } from '@/components/calendar/ModernCalendar'
import { CreateAction } from '@/components/CreateAction'
import { FormError } from '@/components/FormError'
import { page } from '@/components/layout'
import { PhotosIcon } from '@/components/nav/icons'
import { Pills } from '@/components/Pills'
import { Initial } from '@/components/paper'
import { Button } from '@/components/ui/button'
import {
  byDay,
  clockTime,
  entryDetail,
  entryLink,
  entryTitle,
  monthSpan,
  useCircleCalendar,
  useEveryoneCalendar,
  type CalendarEntry,
  type Circle,
  type Missing,
} from '@/lib/calendar'
import { MONTH_NAMES, monthGrid } from '@/lib/dates'
import { type Family } from '@/lib/families'
import { usePageFrame } from '@/lib/frame'
import { localDay } from '@/lib/home'
import { remember, remembered } from '@/lib/remember'
import { circleSections } from '@/lib/sections'
import { useCurrentSpace } from '@/lib/spaces'
import { firstName } from '@/lib/tree'
import { useMe } from '@/lib/useMe'
import { cn } from '@/lib/utils'
import { PlaceHeader, WithFamily } from './FamilyPage'

export const CalendarPage = () => <WithFamily>{(family) => <Calendar family={family} />}</WithFamily>

type Show = 'circle' | 'everyone'
const SHOWS: Show[] = ['circle', 'everyone']
const SHOW_KEY = 'gatheredfrom:calendar-show'
const MONTH = /^(\d{4})-(\d{2})$/
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const link = 'outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50'

// The days that matter to the circle, a month at a time: birthdays and remembrance days from the tree, On this day from
// what was dated to each day in earlier years, and the dates people add themselves. "Everyone" puts every circle you're
// in together, and is all there is in Everyone's Calendar, with dates your connections share with you too. ?date=<id>
// opens an added date; tapping an empty day starts a new one on it.
function Calendar({ family }: { family: Family }) {
  usePageFrame(family.id, { title: 'Calendar' })
  const everyoneOnly = !!family.everyone
  // In the Modern style the owner can pick: who's coming up in rings, then the month's days as cards.
  const me = useMe().data
  const modern = me?.calendarStyle === 'modern'
  // Everyone is offered once you're in a circle or connected with someone.
  const connected = me?.everyone ?? true
  const space = useCurrentSpace(family)
  const [params, setParams] = useSearchParams()
  const today = useMemo(() => localDay(), [])
  const asked = MONTH.exec(params.get('month') ?? '')
  const year = asked ? Number(asked[1]) : Number(today.slice(0, 4))
  const month = asked && Number(asked[2]) >= 1 && Number(asked[2]) <= 12 ? Number(asked[2]) : Number(today.slice(5, 7))
  const [chosen, setShow] = useState<Show>(() => remembered(SHOW_KEY, SHOWS, 'circle'))
  const show: Show = everyoneOnly ? 'everyone' : connected ? chosen : 'circle'
  const [hidden, setHidden] = useState<string[]>([])
  const [picked, setPicked] = useState<string | null>(null)
  const [adding, setAdding] = useState<string | null>(null)
  const openedId = params.get('date')
  // Home's "+" opens a new date straight away (?add=1).
  const asking = params.get('add') === '1'
  const close = () => {
    setAdding(null)
    if (openedId || asking) {
      const next = new URLSearchParams(params)
      next.delete('date')
      next.delete('add')
      setParams(next, { replace: true })
    }
  }
  const { from, days } = monthSpan(year, month)

  const circle = useCircleCalendar(family.id, space?.id, from, days, show === 'circle')
  const everyone = useEveryoneCalendar(from, days, show === 'everyone')
  const data = show === 'circle' ? circle.data : everyone.data
  const error = show === 'circle' ? circle.error : everyone.error
  const circles = everyone.data?.circles ?? []
  // A date shared with you by a connection outside your circles has no circle to hide it by.
  const entries = (data?.entries ?? []).filter(
    (entry) => !entry.circles?.length || entry.circles.some((x) => !hidden.includes(x.id)),
  )
  const daysWith = byDay(entries, from, days)

  const go = (delta: number) => {
    const next = new Date(Date.UTC(year, month - 1 + delta, 1))
    const value = `${next.getUTCFullYear()}-${String(next.getUTCMonth() + 1).padStart(2, '0')}`
    setPicked(null)
    setParams(value === today.slice(0, 7) ? {} : { month: value }, { replace: true })
  }
  const choose = (value: Show) => {
    remember(SHOW_KEY, value)
    setShow(value)
  }
  const pick = (day: string) => {
    if (!daysWith.get(day)?.length) return setAdding(day)
    setPicked(day)
    document.getElementById(`day-${day}`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }
  const listed = [...daysWith].filter(([, x]) => x.length > 0)
  // Birthdays only come from a family tree, so the empty month mentions one only where there is one.
  const withTree = show === 'everyone' || (!!me && circleSections(me.sections, family).people)
  const birthdays = withTree ? ', or a birthday shows here once someone on the tree has a full birth date' : ''
  const title = `${MONTH_NAMES[month - 1]} ${year}`
  // A new date starts on the day picked, else today in this month, else the month's first.
  const startDay = picked ?? (today.slice(0, 7) === from.slice(0, 7) ? today : from)
  const add = () => setAdding(startDay)
  const opened = openedId ? { id: openedId } : adding ? { on: adding } : asking ? { on: startDay } : null

  return (
    <div className={cn(page, 'flex flex-col gap-8 pt-6 pb-10 nav:pt-14')}>
      <PlaceHeader family={family} title="Calendar">
        <p className="mt-1 whisper">
          Birthdays, remembrance days, the dates you add, and what happened on each day, from what’s been kept here.
        </p>
      </PlaceHeader>
      <CreateAction title="Add a date" actions={[{ label: 'Add a date', onSelect: add }]} />
      <DateSheet opened={opened} onClose={close} family={family} space={space} />

      {modern && <ComingUp familyId={family.id} spaceId={space?.id} everyone={show === 'everyone'} today={today} />}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" aria-label="Previous month" onClick={() => go(-1)}>
            <ChevronLeft aria-hidden />
          </Button>
          <h2 className="min-w-40 text-center text-title" aria-live="polite">
            {title}
          </h2>
          <Button variant="ghost" size="icon" aria-label="Next month" onClick={() => go(1)}>
            <ChevronRight aria-hidden />
          </Button>
          {(year !== Number(today.slice(0, 4)) || month !== Number(today.slice(5, 7))) && (
            <Button variant="secondary" size="sm" className="ml-2" onClick={() => setParams({}, { replace: true })}>
              This month
            </Button>
          )}
        </div>
        {connected && !everyoneOnly && (
          <Pills
            label="Show"
            value={show}
            options={[
              ['circle', family.personal ? 'Me' : 'This circle'],
              ['everyone', 'Everyone'],
            ]}
            onChange={choose}
          />
        )}
      </div>

      {show === 'everyone' && circles.length > 1 && (
        <CircleChips
          circles={circles}
          hidden={hidden}
          onToggle={(id) => setHidden(hidden.includes(id) ? hidden.filter((x) => x !== id) : [...hidden, id])}
        />
      )}
      <FormError error={error} />

      {modern ? (
        <div className="flex flex-col gap-6">
          {data && listed.length === 0 && (
            <p className="whisper">
              Nothing falls in {MONTH_NAMES[month - 1]} yet. Add a date{birthdays}.
            </p>
          )}
          <DayCards
            days={listed}
            today={today}
            row={(entry) => <EntryLine entry={entry} familyId={show === 'circle' ? family.id : undefined} />}
          />
          {show === 'circle' && circle.data && circle.data.missing.length > 0 && (
            <MissingDates people={circle.data.missing} />
          )}
        </div>
      ) : (
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_20rem]">
          <MonthGrid
            year={year}
            month={month}
            today={today}
            picked={picked}
            days={daysWith}
            label={title}
            onPick={pick}
          />
          <div className="flex flex-col gap-6">
            <section aria-labelledby="this-month" className="flex flex-col gap-2">
              <h2 id="this-month" className="text-title">
                {MONTH_NAMES[month - 1]}
              </h2>
              {data && listed.length === 0 && (
                <p className="whisper">
                  Nothing falls in {MONTH_NAMES[month - 1]} yet. Tap a day to add a date{birthdays}.
                </p>
              )}
              <ol className="flex flex-col divide-y divide-border/70">
                {listed.map(([day, list]) => (
                  <DayRow
                    key={day}
                    day={day}
                    entries={list}
                    familyId={show === 'circle' ? family.id : undefined}
                    picked={picked === day}
                    today={today === day}
                  />
                ))}
              </ol>
            </section>
            {show === 'circle' && circle.data && circle.data.missing.length > 0 && (
              <MissingDates people={circle.data.missing} />
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// The month as a grid, Sunday first. On a phone each day carries dots; on a tablet or computer, the first few things
// on it in words. A filled dot is a day to celebrate, a ring a remembrance day, a soft dot On this day.
function MonthGrid({
  year,
  month,
  today,
  picked,
  days,
  label,
  onPick,
}: {
  year: number
  month: number
  today: string
  picked: string | null
  days: Map<string, CalendarEntry[]>
  label: string
  onPick: (day: string) => void
}) {
  const cells = monthGrid(year, month)
  const pad = (n: number) => String(n).padStart(2, '0')
  return (
    <div role="grid" aria-label={label} className="flex flex-col gap-1">
      <div role="row" className="grid grid-cols-7 text-center text-sub text-muted-foreground">
        {WEEKDAYS.map((day) => (
          <div role="columnheader" key={day} className="pb-1 font-medium">
            <span aria-hidden className="nav:hidden">
              {day[0]}
            </span>
            <span className="max-nav:sr-only">{day}</span>
          </div>
        ))}
      </div>
      {Array.from({ length: Math.ceil(cells.length / 7) }, (_, week) => (
        <div
          role="row"
          key={week}
          className="grid grid-cols-7 gap-0.5 nav:gap-px nav:bg-border/70 nav:first-of-type:pt-px"
        >
          {cells.slice(week * 7, week * 7 + 7).map((date, i) => {
            if (!date) return <div role="gridcell" key={`b${i}`} className="nav:bg-background" />
            const day = `${year}-${pad(month)}-${pad(date)}`
            const list = days.get(day) ?? []
            const name = new Date(`${day}T00:00:00Z`).toLocaleDateString('en-US', {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
              timeZone: 'UTC',
            })
            return (
              <div
                role="gridcell"
                key={day}
                aria-current={day === today ? 'date' : undefined}
                className="nav:bg-background"
              >
                <button
                  type="button"
                  onClick={() => onPick(day)}
                  aria-label={list.length ? `${name}: ${list.map(entryTitle).join(', ')}` : `${name}: add a date`}
                  aria-pressed={picked === day}
                  className={cn(
                    'flex h-12 w-full flex-col items-center gap-1 rounded-xl pt-1.5 text-sm tabular-nums nav:h-24 nav:items-stretch nav:gap-0.5 nav:rounded-none nav:p-1.5 nav:text-left',
                    'aria-pressed:ring-[1.5px] aria-pressed:ring-brand aria-pressed:ring-inset',
                    link,
                  )}
                >
                  <span
                    className={cn(
                      'grid size-6 place-items-center rounded-full font-medium',
                      day === today && 'bg-brand text-brand-foreground',
                    )}
                  >
                    {date}
                  </span>
                  <Dots entries={list} />
                  <span className="hidden flex-col gap-0.5 nav:flex">
                    {list.slice(0, 2).map((entry, j) => (
                      <span
                        key={j}
                        className={cn(
                          'truncate rounded-md px-1.5 py-0.5 text-xs',
                          entry.kind === 'on-this-day'
                            ? 'px-0 text-muted-foreground'
                            : entry.kind === 'birthday'
                              ? 'bg-secondary'
                              : entry.kind === 'date'
                                ? 'bg-brand/12 text-foreground'
                                : 'border border-dashed border-muted-foreground/60 text-muted-foreground',
                        )}
                      >
                        {short(entry)}
                      </span>
                    ))}
                    {list.length > 2 && <span className="text-xs text-muted-foreground">+{list.length - 2} more</span>}
                  </span>
                </button>
              </div>
            )
          })}
        </div>
      ))}
    </div>
  )
}

// What fits in a day's square on a bigger screen: first names.
function short(entry: CalendarEntry) {
  if (entry.kind === 'on-this-day') return 'On this day'
  if (entry.kind === 'date') {
    const at = entry.date.startsAt && entry.date.dayOf === 1 ? `${clockTime(entry.date.startsAt)} ` : ''
    return `${at}${entry.date.title}`
  }
  const name = firstName(entry.person.name)
  if (entry.kind === 'birthday') return `${name} turns ${entry.years}`
  if (entry.kind === 'remembrance') return `Remembering ${name}`
  return `${name}’s birthday`
}

function Dots({ entries }: { entries: CalendarEntry[] }) {
  return (
    <span aria-hidden className="flex h-1.5 gap-0.5 nav:hidden">
      {entries.slice(0, 3).map((entry, i) => (
        <span
          key={i}
          className={cn(
            'size-1.5 rounded-full',
            entry.kind === 'birthday' && 'bg-warm',
            (entry.kind === 'remembrance' || entry.kind === 'remembered-birthday') && 'border border-muted-foreground',
            entry.kind === 'on-this-day' && 'bg-muted-foreground/50',
            entry.kind === 'date' && 'bg-brand',
          )}
        />
      ))}
    </span>
  )
}

function DayRow({
  day,
  entries,
  familyId,
  picked,
  today,
}: {
  day: string
  entries: CalendarEntry[]
  familyId: string | undefined
  picked: boolean
  today: boolean
}) {
  const date = new Date(`${day}T00:00:00Z`)
  return (
    <li id={`day-${day}`} className={cn('flex scroll-mt-24 gap-3 py-3', picked && 'rounded-xl bg-secondary/60 px-2')}>
      <div className="w-10 shrink-0 text-center leading-tight" aria-hidden>
        <p className="text-[0.6875rem] font-semibold tracking-wide text-muted-foreground uppercase">
          {date.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' })}
        </p>
        <p className={cn('text-lg font-semibold tabular-nums', today && 'text-brand underline underline-offset-4')}>
          {date.getUTCDate()}
        </p>
      </div>
      <ul
        className="flex min-w-0 flex-1 flex-col gap-1"
        aria-label={date.toLocaleDateString('en-US', {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
          timeZone: 'UTC',
        })}
      >
        {entries.map((entry, i) => (
          <li key={i}>
            <EntryLine entry={entry} familyId={familyId} />
          </li>
        ))}
      </ul>
    </li>
  )
}

export function EntryLine({ entry, familyId }: { entry: CalendarEntry; familyId: string | undefined }) {
  const to = entryLink(entry, familyId)
  const remembered = entry.kind === 'remembrance' || entry.kind === 'remembered-birthday'
  const circles = entry.circles?.map((x) => x.name).join(' · ')
  const body = (
    <>
      {entry.kind === 'on-this-day' ? (
        <span aria-hidden className="grid size-9 shrink-0 place-items-center rounded-xl bg-secondary">
          <PhotosIcon className="size-5" />
        </span>
      ) : entry.kind === 'date' ? (
        <span aria-hidden className="grid size-9 shrink-0 place-items-center rounded-full bg-brand/12 text-brand">
          <CalendarDays className="size-5" />
        </span>
      ) : (
        <Initial
          name={entry.person.name}
          portrait={entry.person.portrait}
          className={cn('size-9 shrink-0', remembered && 'opacity-80 grayscale')}
        />
      )}
      <span className="flex min-w-0 flex-col leading-snug">
        <span className={cn('font-medium', remembered && 'text-muted-foreground')}>{entryTitle(entry)}</span>
        <span className="text-sub text-pretty text-muted-foreground">
          {entryDetail(entry)}
          {circles && ` · ${circles}`}
        </span>
      </span>
    </>
  )
  const row = 'flex min-h-11 items-center gap-3 rounded-lg'
  return to ? (
    <Link to={to} className={cn(row, link, 'hover:[&>span:last-child>span:first-child]:underline')}>
      {body}
    </Link>
  ) : (
    <div className={row}>{body}</div>
  )
}

function CircleChips({
  circles,
  hidden,
  onToggle,
}: {
  circles: Circle[]
  hidden: string[]
  onToggle: (id: string) => void
}) {
  return (
    <div role="group" aria-label="Circles" className="-mt-4 flex flex-wrap gap-2">
      {circles.map((circle) => (
        <button
          key={circle.id}
          type="button"
          aria-pressed={!hidden.includes(circle.id)}
          onClick={() => onToggle(circle.id)}
          className={cn(
            'flex min-h-9 items-center rounded-full border px-3.5 text-sm text-muted-foreground transition-colors pointer-coarse:min-h-11',
            'aria-pressed:border-brand aria-pressed:text-brand',
            link,
          )}
        >
          {circle.name}
        </button>
      ))}
    </div>
  )
}

// People you look after with no full birthday yet: a year alone ("1958") can't be put on a day, so it waits for
// whoever knows it. Never guessed.
function MissingDates({ people }: { people: Missing[] }) {
  const shown = people.slice(0, 6)
  return (
    <section aria-labelledby="missing" className="flex flex-col gap-2 rounded-2xl border p-4">
      <h2 id="missing" className="text-body font-semibold">
        Birthdays to add
      </h2>
      <p className="text-sub text-muted-foreground">Only people you look after are listed.</p>
      <ul className="flex flex-col">
        {shown.map((x) => (
          <li key={x.id}>
            <Link to={`/person/${x.id}`} className={cn('flex min-h-11 items-center gap-3 rounded-lg', link)}>
              <Initial name={x.name} portrait={x.portrait} className="size-8 shrink-0" />
              <span className="flex min-w-0 flex-col leading-snug">
                <span className="truncate font-medium">{x.name}</span>
                <span className="text-sub text-muted-foreground">
                  {x.born ? `Born in ${bornLabel(x.born)} · add the day` : 'Add the day'}
                </span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
      {people.length > shown.length && (
        <p className="text-sub text-muted-foreground">and {people.length - shown.length} more</p>
      )}
    </section>
  )
}

const bornLabel = (born: string) => {
  const [y, m] = born.split('-').map(Number)
  return m ? `${MONTH_NAMES[m - 1]} ${y}` : String(y)
}
